import { inject, observer } from 'mobx-react'
import React, { useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router'
import { toast } from 'react-toastify'
import { Loader } from 'semantic-ui-react'
import { sortByStatus } from '../../../core/helpers'
import AngentTable from './AgentTable'
import AgenTabs, { MENU_ITEMS } from './AgentTabs'
import BotTable from './BotTable'
import Confirm from './Confirm'
import { deleteConversationsWarning, withConversationRemoved, withoutAgent } from './agentListHelpers'

const STANDARD_BOTS = 'Standard bots'
const PREMIUM_BOTS = 'Premium bots'
const AGENTS = 'Agents'

const AgentList = inject('actions')(observer(({ actions }) => {
  const history = useHistory()
  const location = useLocation()

  const [agents, setAgents] = useState([])
  const [loading, setLoading] = useState(false)
  const [confirm, setConfirm] = useState({})

  const [options, setOptions] = useState({
    includeDeleted: false,
    includeDeactivated: false,
  })

  useEffect(() => {
    const qp = new URLSearchParams(location.search)
    const opts = {
      includeDeleted: false,
      includeDeactivated: qp.get('includeDeactivated') === 'true',
      includeBots: true,
      includeStandardAgents: true,
    }
    if(qp.has('updated') || qp.has('created')) {
      toast.info(`Agent ${qp.has('created') ? 'created' : 'updated'}`)
      qp.delete('updated')
      qp.delete('created')
      history.replace(`/admin/agents${qp.toString()}`)
    }
    setOptions(opts)
    listAgents(opts)
  }, [])

  const listAgents = async(opts = options) => {
    setLoading(true)
    try {
      const agentResponse = await actions.agent.listAgents(opts)
      const agentListCopy = [...agentResponse]
      setAgentsSorted(agentListCopy)
    } catch (error) {
      toast.error("Failder to fetch agents list.")
    } finally {
      setLoading(false)
    }
  }

  const toggleCheckboxFilter = (e, { name, checked }) => {
    const params = new URLSearchParams(location.search)
    params.set(name, checked)
    const opts = {...options, [name]: checked }
    setOptions(opts)
    history.replace({ pathname: location.pathname, search: params.toString() })
    listAgents(opts)
  }

  const toggleAgentActiveState = (agentId, newStatus) => {
    const index = agents.findIndex(a => a.id === agentId)
    setConfirm({
      title: 'Toggle agent status',
      onCancel: () => setConfirm({}),
      onSuccess: () => actions.agent.setActiveStatus(agentId, newStatus).then(([, e]) => {
        setConfirm({})
        if(e) {
          toast.error(`There was an error toggling the ${agents[index].name}' status`)
          return
        }
        toast.success(`${agents[index].name} ${newStatus ? 'activated': 'deactivated'} successfully`)
        const agentsCopy = [...agents]
        agentsCopy[index].isActive = newStatus
        setAgentsSorted(agentsCopy)
      }),
      children: <p>Are you sure you want to {newStatus ? 'activate': 'deactivate' } {agents[index].name}?</p>,
    })
  }

  const detachAll = async(agentId)  => {
    const index = agents.findIndex(a => a.id === agentId)
    setConfirm({
      title: 'Detach all conversations',
      onCancel: () => setConfirm({}),
      onSuccess: () => actions.agent.detachAll(agentId).then(([, e]) => {
        setConfirm({})
        if(e) {
          toast.error(`There was an error detaching all conversations of ${agents[index].name}`)
          return
        }
        toast.success(`Detached all conversations of ${agents[index].name}`)
        const agentsCopy = [...agents]
        agentsCopy[index].conversationCount = 0
        setAgentsSorted(agentsCopy)
      }),
      children: <p>Are you sure you want to detach all conversations of {agents[index].name}?</p>,
    })
  }

  const forcefullyLogoutAgent = async(agentId) => {
    const index = agents.findIndex(a => a.id === agentId)
    setConfirm({
      title: 'Logout agent',
      onCancel: () => setConfirm({}),
      onSuccess: () => actions.agent.forceFullyLogout(agentId).then(([, e]) => {
        setConfirm({})
        if(e) {
          toast.error(`There was an error logging out ${agents[index].name}`)
          return
        }
        toast.success(`Logged out ${agents[index].name}`)
        const agentsCopy = [...agents]
        agentsCopy[index].status = 'offline'
        setAgentsSorted(agentsCopy)
      }),
      children: <p>Are you sure you want to logout {agents[index].name}?</p>,
    })
  }

  const deleteAgent = async(agentId) => {
    const index = agents.findIndex(a => a.id === agentId)
    const conversationsWarning = deleteConversationsWarning(agents[index])
    setConfirm({
      title: 'Delete agent',
      onCancel: () => setConfirm({}),
      onSuccess: () => actions.agent.delete(agentId).then(([, e]) => {
        setConfirm({})
        if (e) {
          toast.error(`There was an error deleting ${agents[index].name}`)
          return
        }
        toast.success(`${agents[index].name} deleted`)
        setAgentsSorted(withoutAgent(agents, agentId))
      }),
      children: <>
        {conversationsWarning && <p>{conversationsWarning}</p>}
        <p>Delete {agents[index].name}? This is final: the account is removed from CCP admin and its email is released so it can be used for a new account. Conversation history is kept.</p>
      </>,
    })
  }

  const setAgentsSorted = (agentsPayload) => {
    setAgents(sortByStatus(agentsPayload))
  }

  // Uses the latest list, as it is called after a request that may finish after other changes
  const onConversationRemoved = (agentId) => {
    setAgents((current) => sortByStatus(withConversationRemoved(current, agentId)))
  }

  if(loading) {
    return (
      <Loader active={true}/>
    )
  }

  return (
    <div className='content'>
      <AgenTabs render={(activeTab) => {
        if(activeTab === MENU_ITEMS.botAgents) {
          const botList = agents.filter((agnt) => agnt.type !== 'agent')
          const premiumBots = botList.filter((bl) => bl.objectives.length === 0)
          const standardBots = botList.filter((bl) => bl.objectives.length > 0)
          return (
            <>
              <BotTable
                title={PREMIUM_BOTS}
                botsList={premiumBots}
                options={options}
                toggleCheckboxFilter={toggleCheckboxFilter}
                toggleAgentActiveState={toggleAgentActiveState}
                detachAll={detachAll}
                forcefullyLogoutAgent={forcefullyLogoutAgent}
                deleteAgent={deleteAgent}
                onConversationRemoved={onConversationRemoved}
              />
              <BotTable
                title={STANDARD_BOTS}
                botsList={standardBots}
                isPremium={false}
                toggleCheckboxFilter={toggleCheckboxFilter}
                toggleAgentActiveState={toggleAgentActiveState}
                detachAll={detachAll}
                forcefullyLogoutAgent={forcefullyLogoutAgent}
                deleteAgent={deleteAgent}
                onConversationRemoved={onConversationRemoved}
              />
            </>
          )
        }
        const agentList = agents.filter((ag) => ag.type === 'agent')
        return (
          <AngentTable
            title={AGENTS}
            agents={agentList}
            options={options}
            toggleCheckboxFilter={toggleCheckboxFilter}
            toggleAgentActiveState={toggleAgentActiveState}
            detachAll={detachAll}
            forcefullyLogoutAgent={forcefullyLogoutAgent}
            deleteAgent={deleteAgent}
            onConversationRemoved={onConversationRemoved}
          />
        )
      }}/>
      {Object.keys(confirm).length > 0 && <Confirm {...confirm}/>}
    </div>
  )
}))

export default boundary()(inject('store', 'actions')(observer(AgentList)))
